import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlignCenter,
  Bold,
  Italic,
  Redo2,
  RotateCcw,
  Save,
  Stamp,
  Undo2,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import Button from '../../components/Button'
import InfoHint from '../../components/InfoHint'
import Select from '../../components/Select'
import { useHint } from '../../components/useHint'
import useBodyScrollLock from '../../components/useBodyScrollLock'
import { certificatesStrings as S } from './strings'
import './CertificateEditor.css'
import { getTemplateLayout } from './contentService'

const E = S.editor

/**
 * A toolbar button whose only label is its tooltip.
 *
 * `title` gave these nine glyphs an operating-system bubble after a second's wait, and a
 * keyboard nothing at all — on the one toolbar in the panel where the icon IS the entire
 * control. The app's own bubble arrives at once, and `aria-label` makes the name real
 * rather than a hint.
 */
function Tool({ label, className = 'cert-studio__tgl', children, ...rest }) {
  const { hint, bubble } = useHint(label)

  return (
    <>
      <button type="button" className={className} aria-label={label} {...hint} {...rest}>
        {children}
      </button>
      {bubble}
    </>
  )
}

/** The artwork's own geometry — the legacy requires a 1528x1080 PNG exactly. */
const SHEET = { width: 1528, height: 1080 }

/* THE LEGACY'S FIVE, VERBATIM — `#font-family` in Index.cshtml, whose options carry no
   `value`, so what is stored is the bare family name: "Times New Roman", not a CSS stack.
   These used to be stacks, so a certificate saved as "Times New Roman" matched no option
   and the picker came up BLANK on every layer that had ever been saved; picking anything
   then wrote a stack back, which the certificate generator cannot resolve.

   `San-serif` is the legacy's own misspelling. It is kept as the stored value — changing it
   would write a family a legacy installation has never seen — and corrected on the way to
   the screen by `cssFamily`, so the option renders as something instead of nothing. */
const FONTS = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Calibri', label: 'Calibri' },
  { value: 'San-serif', label: 'San-serif' },
  { value: 'Tahoma', label: 'Tahoma' },
  { value: 'Times New Roman', label: 'Times New Roman' },
]

/** The stored family, as something a browser will actually render. */
const cssFamily = (name) => {
  if (name === 'San-serif') return 'sans-serif'
  const generic = name === 'Times New Roman' ? 'serif' : 'sans-serif'
  return `"${name}", ${generic}`
}

/**
 * The four layers the legacy paints over the artwork.
 *
 * `centered` is per layer, not a blanket default: the name and the programme
 * are centred on the sheet, but the date and the code sit in opposite corners
 * of the footer.
 *
 * The text is the LEGACY'S OWN SEED — "انقر مرتين لتعديل الاسم" — not a specimen name. It
 * reads as an instruction, which is the only hint either editor gives that a layer can be
 * typed over at all; and because an auto-centred layer's stored X is computed from whatever
 * text is standing in for the real one, using the same seed keeps that number where a
 * legacy installation put it instead of shifting it on the first save.
 */
const LAYERS = [
  { id: 'name', label: () => E.name, text: () => E.seed.name, x: 50, y: 46, size: 42, bold: true, centered: true },
  { id: 'program', label: () => E.program, text: () => E.seed.program, x: 50, y: 60, size: 26, centered: true },
  { id: 'date', label: () => E.date, text: () => E.seed.date, x: 28, y: 82, size: 18 },
  { id: 'code', label: () => E.code, text: () => E.seed.code, x: 72, y: 82, size: 18 },
]

const initialLayout = () =>
  Object.fromEntries(
    LAYERS.map((layer) => [
      layer.id,
      {
        text: layer.text(),
        x: layer.x,
        y: layer.y,
        size: layer.size,
        font: 'Calibri', // the legacy's `selected` option
        color: '#38761D',
        bold: Boolean(layer.bold),
        italic: false,
        align: 'center',
        centered: Boolean(layer.centered),
        offset: 0,
        visible: true,
      },
    ]),
  )

/** Snap threshold, in sheet percent. */
const SNAP = 1.2

/* The selection box's own padding and border, in SCREEN pixels.
   A fabric.IText has no chrome — its `left`/`top` IS the first glyph's corner — so the box
   drawn around a layer here is shifted back by exactly its own inset, and the text lands on
   the stored coordinate at any zoom. Screen pixels, not sheet pixels, because the chrome is
   interface: it must stay 1px hairline whether the sheet is drawn at 40% or 200%. */
const CHROME_X = 7
const CHROME_Y = 3

/** One sheet pixel, as a percentage of each axis — the legacy's arrow-key nudge. */
const NUDGE = { x: 100 / SHEET.width, y: 100 / SHEET.height }

/**
 * The certificate layout editor.
 *
 * The legacy is a Fabric.js canvas behind a strip of bare `B` / `I` / `C` chips
 * and two labels ("Font", "Color") that were never translated: you pick a layer
 * by clicking blind on a 1528px bitmap, and the only way to know what is
 * selected is that something moved. It also has no zoom, no undo, no way to
 * hide a layer, and no alignment aid of any kind.
 *
 * This keeps everything the legacy can do — drag, per-layer font / size /
 * colour / bold / italic, horizontal centring with a pixel `إزاحة`, and text
 * you edit in place by double-clicking — and adds what a placement tool needs
 * to be usable: a named layer list, zoom, undo/redo, snap guides against the
 * sheet's centre lines and against the other layers, per-layer visibility, and
 * arrow-key nudging, because a certificate is aligned to within a pixel or two
 * and a pointer cannot do that.
 *
 * The SHAPE is the legacy's, deliberately: one tools strip across the top and the
 * sheet filling the rest. It was three panels around a sheet squeezed into the
 * middle third, which is a worse tool for the one job this screen has.
 *
 * Internally a position is a PERCENTAGE of the sheet, so a layout is independent
 * of the zoom it was placed at; the legacy stores absolute pixels against a fixed
 * 1528x1080, which is why its artwork spec is exact to the pixel. `contentService`
 * converts at the boundary, and — as in the legacy — a coordinate is the TEXT'S
 * TOP-LEFT CORNER, not its middle.
 */
export default function CertificateEditor({ open, certificate, language, onClose, onSave }) {
  const [layout, setLayout] = useState(initialLayout)
  const [selected, setSelected] = useState('name')
  const [editing, setEditing] = useState(null)
  const [zoom, setZoom] = useState(1)
  const [artwork, setArtwork] = useState(null)
  const [guides, setGuides] = useState({ x: null, y: null })

  /* Undo history holds committed layouts only — a drag pushes one entry when it
     ends, not one per pointermove. */
  const [history, setHistory] = useState([])
  const [future, setFuture] = useState([])

  const stageRef = useRef(null)
  const sheetRef = useRef(null)
  const dragRef = useRef(null)
  /* Measured on save: a centred layer's stored X depends on how wide its own text renders. */
  const layerRefs = useRef({})

  useBodyScrollLock(open)

  useEffect(() => {
    if (!open) return undefined

    const defaults = initialLayout()
    setLayout(defaults)
    setSelected('name')
    setEditing(null)
    setHistory([])
    setFuture([])
    setGuides({ x: null, y: null })
    /* THE ARTWORK COMES FROM THE CERTIFICATE, which is the whole order of the legacy's
       flow: the PNG is uploaded on the certificate's own form — validated there as PNG at
       exactly 1528x1080 — and only then is there a sheet to place text on. The editor used
       to carry its own file picker that read a local file into a data URL: it changed what
       you saw and nothing else, so a layout was placed against one image and printed
       against another. */
    const source =
      (language === 'english' ? certificate?.englishTemplate : certificate?.arabicTemplate) ?? null

    /* PROBED, not trusted. `GetAllCertificates` builds `root/CTemplate/{PathMap}` from the
       row alone, so it returns a path for every certificate whether or not a PNG was ever
       written there — and on this installation none was (API_FINDINGS §97: the upload lands
       outside `wwwroot`). Assigning the URL blind gave a sheet with a broken image behind it
       and nothing on screen saying why. */
    setArtwork(null)
    let stale = false
    if (source) {
      const probe = new Image()
      probe.onload = () => {
        if (!stale) setArtwork(source)
      }
      probe.src = source
    }

    if (!certificate?.id) {
      return () => {
        stale = true
      }
    }

    /**
     * The saved positions, per language.
     *
     * MERGED over the defaults rather than replacing them: `TemplatePropertiesDto` carries
     * font, colour, size, position and the three flags — but no sample TEXT, no alignment
     * and no visibility, which are the editor's own. Replacing wholesale would blank the
     * layers.
     */
    let cancelled = false
    getTemplateLayout(certificate.id, language === 'english' ? 'english' : 'arabic')
      .then((saved) => {
        if (cancelled || !saved) return
        setLayout((current) =>
          Object.fromEntries(
            Object.entries(current).map(([id, layer]) => [id, { ...layer, ...(saved[id] ?? {}) }]),
          ),
        )
      })
      .catch(() => {
        /* A certificate whose layout has never been saved has none; the defaults stand. */
      })

    return () => {
      cancelled = true
      stale = true
    }
  }, [open, certificate, language])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape' && !editing) onClose?.()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, editing, onClose])

  const current = layout[selected]

  /** Snapshots the layout before a change, so the change can be undone. */
  const commit = useCallback(() => {
    setHistory((previous) => [...previous.slice(-49), layout])
    setFuture([])
  }, [layout])

  const patch = (values, { record = true } = {}) => {
    if (record) commit()
    setLayout((previous) => ({ ...previous, [selected]: { ...previous[selected], ...values } }))
  }

  const undo = () => {
    setHistory((previous) => {
      if (previous.length === 0) return previous
      const last = previous[previous.length - 1]
      setFuture((ahead) => [layout, ...ahead])
      setLayout(last)
      return previous.slice(0, -1)
    })
  }

  const redo = () => {
    setFuture((ahead) => {
      if (ahead.length === 0) return ahead
      setHistory((previous) => [...previous, layout])
      setLayout(ahead[0])
      return ahead.slice(1)
    })
  }

  /* ---- Dragging ---------------------------------------------------------
     The sheet is the frame of reference, so a position stays a percentage and
     survives both a resize and a zoom. */
  const onPointerDown = (id) => (event) => {
    if (editing) return
    setSelected(id)
    const sheet = sheetRef.current
    if (!sheet) return
    event.currentTarget.setPointerCapture(event.pointerId)
    commit()
    /* Grab the layer where it was actually grabbed — otherwise it jumps its own half-width
       under the cursor the moment a drag starts. */
    const box = event.currentTarget.getBoundingClientRect()
    dragRef.current = {
      id,
      rect: sheet.getBoundingClientRect(),
      grabX: event.clientX - box.left,
      grabY: event.clientY - box.top,
    }
  }

  const onPointerMove = useCallback(
    (event) => {
      const drag = dragRef.current
      if (!drag) return

      /* `+ CHROME` puts back the inset `layerStyle` takes off, so what is tracked is the
         TEXT's corner and a layer does not creep by its own border every time it is
         picked up. */
      let x = ((event.clientX - drag.grabX + CHROME_X - drag.rect.left) / drag.rect.width) * 100
      let y = ((event.clientY - drag.grabY + CHROME_Y - drag.rect.top) / drag.rect.height) * 100

      /* Snap to the sheet's centre line and to any other layer's edge, and show the line
         that was snapped to — the legacy offers no alignment aid at all, so "is this
         centred" was a question you answered by eye.

         Horizontally the test is against the layer's MIDDLE, because that is what centred
         means to a reader; the value stored is still its left edge. */
      const box = layerRefs.current[drag.id]?.getBoundingClientRect()
      const halfWidth = box ? ((box.width - CHROME_X * 2) / drag.rect.width) * 50 : 0
      const others = Object.entries(layout).filter(([id]) => id !== drag.id)
      const centred = Math.abs(x + halfWidth - 50) < SNAP
      const nearX = others.map(([, item]) => item.x).find((value) => Math.abs(value - x) < SNAP)
      const nearY = [50, ...others.map(([, item]) => item.y)].find(
        (value) => Math.abs(value - y) < SNAP,
      )
      if (centred) x = 50 - halfWidth
      else if (nearX != null) x = nearX
      if (nearY != null) y = nearY
      setGuides({ x: centred ? 50 : (nearX ?? null), y: nearY ?? null })

      setLayout((previous) => ({
        ...previous,
        [drag.id]: {
          ...previous[drag.id],
          x: Math.min(100, Math.max(0, Math.round(x * 10) / 10)),
          y: Math.min(100, Math.max(0, Math.round(y * 10) / 10)),
          /* Dragging horizontally IS an explicit placement, so it takes the layer off
             auto-centring rather than fighting it next render — unless it was dropped ON
             the centre line, which is the same thing the `C` button does. */
          centered: centred,
          offset: centred ? 0 : previous[drag.id].offset,
        },
      }))
    },
    [layout],
  )

  const endDrag = useCallback(() => {
    dragRef.current = null
    setGuides({ x: null, y: null })
  }, [])

  /* One sheet pixel per press, ten with Shift — the legacy's own `object.left -= 1`, which
     is the only way it can place anything exactly and the reason the arrow keys are here. */
  const onKeyDown = (event) => {
    if (editing) return
    const step = event.shiftKey ? 10 : 1
    const moves = {
      ArrowUp: { y: -step * NUDGE.y },
      ArrowDown: { y: step * NUDGE.y },
      ArrowLeft: { x: -step * NUDGE.x },
      ArrowRight: { x: step * NUDGE.x },
    }
    const move = moves[event.key]
    if (!move) return
    event.preventDefault()
    patch({
      x: Math.min(100, Math.max(0, Math.round((current.x + (move.x ?? 0)) * 10) / 10)),
      y: Math.min(100, Math.max(0, Math.round((current.y + (move.y ?? 0)) * 10) / 10)),
      ...(move.x ? { centered: false } : null),
    })
  }

  /**
   * Fits the whole sheet on screen.
   *
   * This set the zoom to 1, which is not a fit: the sheet is 1080px tall and the stage under
   * a toolbar on a laptop is nearer 700, so the editor opened showing the top two thirds of
   * a certificate and the footer layers — the date and the code — were below the fold, on
   * the wrong side of a scrollbar, on the screen whose entire job is placing them.
   */
  const fit = useCallback(() => {
    const stage = stageRef.current
    if (!stage) return
    const style = getComputedStyle(stage)
    const width = stage.clientWidth - parseFloat(style.paddingInline || 0) * 2
    const height = stage.clientHeight - parseFloat(style.paddingBlock || 0) * 2
    if (width <= 0 || height <= 0) return
    const scale = Math.min(width / SHEET.width, height / SHEET.height)
    setZoom(Math.max(0.4, Math.min(2, Math.round(scale * 100) / 100)))
  }, [])

  /* Fit on open, and again whenever the window changes shape. */
  useEffect(() => {
    if (!open) return undefined
    const frame = requestAnimationFrame(fit)
    window.addEventListener('resize', fit)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', fit)
    }
  }, [open, fit])

  const layerStyle = (style) => ({
    /* TOP-LEFT, and PHYSICAL left/top rather than the logical properties.
       A `fabric.IText` is built with fabric's default origin, so `NamePositionX` /
       `NamePositionY` are the corner of the text — not its middle. This placed the stored
       corner at the layer's CENTRE, so every layer sat up and across by half its own box,
       and the further off-centre it was the worse it read. And `insetInlineStart` measures
       from the RIGHT under the Arabic UI: the same saved layout came out mirrored between
       the two languages, on a canvas the legacy treats as left-to-right always. */
    left: style.centered ? '50%' : `${style.x}%`,
    top: `${style.y}%`,
    /* Centring is the legacy's `centerObjectH`: `left = canvasWidth/2 - objectWidth/2 +
       offset`. Expressed as a translate the box centres itself, so its width never has to
       be known here; the offset is in SHEET pixels, so it is converted to `cqw` and scales
       with the sheet instead of drifting as you zoom. */
    translate: style.centered
      ? `calc(-50% + ${(style.offset / SHEET.width) * 100}cqw) -${CHROME_Y}px`
      : `-${CHROME_X}px -${CHROME_Y}px`,
    /* Sizes are stored against the 1080px sheet, so they are expressed as a
       share of the sheet's height and scale with the zoom for free. */
    fontSize: `${(style.size / SHEET.height) * 100}cqh`,
    fontFamily: cssFamily(style.font),
    color: style.color,
    fontWeight: style.bold ? 700 : 400,
    fontStyle: style.italic ? 'italic' : 'normal',
    textAlign: style.align,
  })

  /**
   * The layout as the SERVER should hold it.
   *
   * A centred layer has no X of its own on screen — it is placed by the translate above —
   * but the legacy stores one, computed from the text's own width, and the certificate
   * generator reads that number back as a plain corner. So it is measured here, through the
   * same formula `centerObjectH` uses, rather than saving whatever X the layer happened to
   * carry before it was centred.
   */
  const persistable = () => {
    const sheet = sheetRef.current
    if (!sheet) return layout
    const sheetWidth = sheet.getBoundingClientRect().width
    if (!sheetWidth) return layout

    return Object.fromEntries(
      Object.entries(layout).map(([id, style]) => {
        const element = layerRefs.current[id]
        if (!style.centered || !element) return [id, style]
        const width = (element.getBoundingClientRect().width / sheetWidth) * 100
        const offset = (style.offset / SHEET.width) * 100
        return [id, { ...style, x: Math.round((50 - width / 2 + offset) * 10) / 10 }]
      }),
    )
  }

  const fonts = useMemo(() => FONTS, [])

  if (!open) return null

  return (
    <div className="cert-studio" role="dialog" aria-modal="true" aria-label={E.title}>
      {/* ONE STRIP, then the sheet — the legacy's `#drawingScreen`: a single `.tools-container`
          row across the top and `.canvas-container` filling everything under it. This was a
          header plus a layer rail plus a properties panel, three columns squeezing the sheet
          into the middle third, which is a different tool from the one it replaces.
          Everything those panels held is still here; a certificate is placed by looking at
          the certificate, so the certificate gets the room. */}
      <div className="cert-studio__toolbar">
        {/* WHICH LAYER. The legacy has no picker — you click blind on a 1528px bitmap and
            learn what you selected by seeing something move. Clicking the text still selects
            it; this only says which one is live, and reaches one that sits under another. */}
        <div className="cert-studio__seg cert-studio__seg--layers">
          {LAYERS.map((layer) => {
            const style = layout[layer.id]
            return (
              <button
                key={layer.id}
                type="button"
                className={`cert-studio__chip${selected === layer.id ? ' is-on' : ''}${
                  style.visible ? '' : ' is-off'
                }`}
                aria-pressed={selected === layer.id}
                onClick={() => setSelected(layer.id)}
                onDoubleClick={() => {
                  commit()
                  setLayout((previous) => ({
                    ...previous,
                    [layer.id]: { ...previous[layer.id], visible: !previous[layer.id].visible },
                  }))
                }}
                title={style.visible ? `${layer.label()} — ${E.hide}` : `${layer.label()} — ${E.show}`}
              >
                <bdi>{layer.label()}</bdi>
              </button>
            )
          })}
        </div>

        <span className="cert-studio__rule" aria-hidden="true" />

        <label className="cert-studio__field">
          <span>{E.font}</span>
          <Select value={current.font} onChange={(font) => patch({ font })} options={fonts} />
        </label>

        <label className="cert-studio__field cert-studio__field--color">
          <span>{E.color}</span>
          <input
            type="color"
            value={current.color}
            onChange={(event) => patch({ color: event.target.value })}
            aria-label={E.color}
          />
        </label>

        <label className="cert-studio__field cert-studio__field--num">
          <span>{E.size}</span>
          <input
            type="number"
            min={8}
            max={160}
            value={current.size}
            onChange={(event) => patch({ size: Number(event.target.value) })}
            aria-label={E.size}
          />
        </label>

        {/* The legacy's bare `B` / `I` / `C` spans, as real toggles that show their state. */}
        <div className="cert-studio__seg" role="group" aria-label={E.style}>
          <Tool
            className={`cert-studio__tgl${current.bold ? ' is-on' : ''}`}
            aria-pressed={current.bold}
            label={E.bold}
            onClick={() => patch({ bold: !current.bold })}
          >
            <Bold size={15} strokeWidth={2.4} />
          </Tool>
          <Tool
            className={`cert-studio__tgl${current.italic ? ' is-on' : ''}`}
            aria-pressed={current.italic}
            label={E.italic}
            onClick={() => patch({ italic: !current.italic })}
          >
            <Italic size={15} strokeWidth={2.4} />
          </Tool>
          <Tool
            className={`cert-studio__tgl${current.centered ? ' is-on' : ''}`}
            aria-pressed={current.centered}
            label={E.center}
            onClick={() => patch({ centered: !current.centered })}
          >
            <AlignCenter size={15} strokeWidth={2.4} />
          </Tool>
        </div>

        {/* `إزاحة` — a pixel nudge off the sheet's centre. The legacy shows this box always;
            it only means anything while the layer IS centred, so it appears with the state it
            belongs to instead of sitting there inert. */}
        {current.centered && (
          <label className="cert-studio__field cert-studio__field--num">
            <span>
              {E.offset}
              <InfoHint content={E.offsetHint} />
            </span>
            <input
              type="number"
              min={-2000}
              max={2000}
              value={current.offset}
              onChange={(event) => patch({ offset: Number(event.target.value) })}
              aria-label={E.offset}
            />
          </label>
        )}

        <span className="cert-studio__rule" aria-hidden="true" />

        <div className="cert-studio__seg">
          <Tool label={E.undo} onClick={undo} disabled={history.length === 0}>
            <Undo2 size={15} strokeWidth={1.9} />
          </Tool>
          <Tool label={E.redo} onClick={redo} disabled={future.length === 0}>
            <Redo2 size={15} strokeWidth={1.9} />
          </Tool>
        </div>

        <div className="cert-studio__seg">
          <Tool
            label={E.zoomOut}
            onClick={() => setZoom((z) => Math.max(0.4, Math.round((z - 0.1) * 10) / 10))}
          >
            <ZoomOut size={15} strokeWidth={1.9} />
          </Tool>
          <Tool
            className="cert-studio__tgl cert-studio__tgl--wide"
            label={E.zoomFit}
            onClick={fit}
          >
            {Math.round(zoom * 100)}%
          </Tool>
          <Tool
            label={E.zoomIn}
            onClick={() => setZoom((z) => Math.min(2, Math.round((z + 0.1) * 10) / 10))}
          >
            <ZoomIn size={15} strokeWidth={1.9} />
          </Tool>
        </div>

        <Tool
          label={E.reset}
          onClick={() => {
            commit()
            setLayout(initialLayout())
          }}
        >
          <RotateCcw size={15} strokeWidth={1.9} />
        </Tool>

        {/* Save and close close the row, as they do in the legacy. */}
        <div className="cert-studio__end">
          <Button size="sm" onClick={() => onSave?.(persistable())}>
            <Save size={15} strokeWidth={1.9} />
            {S.form.save}
          </Button>
          <Button size="sm" variant="danger" onClick={onClose}>
            <X size={15} strokeWidth={2} />
            {E.close}
          </Button>
        </div>
      </div>

      {/* The one line of context the strip cannot carry: whose certificate, and in which
          language — the two things that decide what a save overwrites. */}
      <p className={`cert-studio__meta${artwork ? '' : ' cert-studio__meta--missing'}`}>
        <Stamp size={14} strokeWidth={1.75} aria-hidden="true" />
        <bdi>{certificate?.name}</bdi>
        <span className="cert-studio__meta-lang">
          {language === 'english' ? S.row.englishTemplate : S.row.arabicTemplate}
        </span>
        {!artwork && <bdi className="cert-studio__meta-warn">{E.noArtwork}</bdi>}
      </p>

      <main className="cert-studio__stage" ref={stageRef}>
        <div
          className={`cert-studio__sheet${artwork ? ' has-artwork' : ''}`}
          ref={sheetRef}
          style={{
            width: `${SHEET.width * zoom}px`,
            aspectRatio: `${SHEET.width} / ${SHEET.height}`,
            backgroundImage: artwork ? `url(${artwork})` : undefined,
          }}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          {guides.x != null && (
            <span
              className="cert-studio__guide cert-studio__guide--v"
              style={{ insetInlineStart: `${guides.x}%` }}
            />
          )}
          {guides.y != null && (
            <span
              className="cert-studio__guide cert-studio__guide--h"
              style={{ insetBlockStart: `${guides.y}%` }}
            />
          )}

          {LAYERS.map((layer) => {
            const style = layout[layer.id]
            if (!style.visible) return null
            const isEditing = editing === layer.id
            return (
              <div
                key={layer.id}
                ref={(element) => {
                  layerRefs.current[layer.id] = element
                }}
                className={`cert-studio__text${selected === layer.id ? ' is-selected' : ''}${
                  isEditing ? ' is-editing' : ''
                }`}
                style={layerStyle(style)}
                tabIndex={0}
                role="button"
                aria-label={layer.label()}
                title={E.editText}
                onPointerDown={isEditing ? undefined : onPointerDown(layer.id)}
                onKeyDown={selected === layer.id ? onKeyDown : undefined}
                onDoubleClick={() => {
                  setSelected(layer.id)
                  setEditing(layer.id)
                }}
              >
                {/* Double-click to type over it, exactly as the legacy's `fabric.IText`
                    layers work. */}
                <span
                  className="cert-studio__text-inner"
                  dir="auto"
                  contentEditable={isEditing}
                  suppressContentEditableWarning
                  onBlur={(event) => {
                    const text = event.currentTarget.textContent ?? ''
                    setEditing(null)
                    commit()
                    setLayout((previous) => ({
                      ...previous,
                      [layer.id]: { ...previous[layer.id], text },
                    }))
                  }}
                >
                  {style.text}
                </span>
              </div>
            )
          })}
        </div>
      </main>
    </div>
  )
}
